import Dexie, { Table } from 'dexie';

// ============================================
// Interfaces pour les entités IndexedDB
// ============================================

// Interface de base pour toutes les entités synchronisables
interface BaseSyncEntity {
  id?: number;
  localId?: string;
  syncStatus: 'synced' | 'pending' | 'conflict' | 'error';
  lastSync?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  version?: number;
}

export interface ContratLocal extends BaseSyncEntity {
  codeemp?: string;
  matricule?: string;
  loyer?: number;
  solde_initial?: number;
  type_solde?: string;
  date_creation?: Date;
  date_signature?: Date;
  date_resiliation?: Date;
  duree?: string;
  reference?: string;
  observation?: string;
  motif_resiliation?: string;
  etape_validation?: boolean;
  est_valide?: boolean;
  locataire?: string;
  num_compte?: string;
  // Relations
  codeempData?: any;
  matriculeData?: any;
  userCreation?: any;
}

export interface PaiementLocal extends BaseSyncEntity {
  contrat_id?: number;
  montantPaye?: number;
  periode?: string;
  nombreMois?: number;
  modePaiement?: number | any;
  datePaiement?: Date;
  refPaiement?: string;
  etat?: number;
  statut?: string;
  transactionId?: string;
  annee?: number;
  mois?: string;
  montantRestant?: number;
  montantRecu?: number;
  motifAnnulation?: string;
  dateAnnulation?: Date;
  motifRejet?: string;
  // Relations
  contrat_idData?: any;
  modePaiementData?: any;
  userCreation?: any;
  userAgenceCreate?: any;
  userRegularisation?: any;
  userAnnulation?: any;
  medias?: any[];
}

export interface PaiementRecetteLocal extends BaseSyncEntity {
  contrat_id?: number;
  montant?: number;
  refPaiement?: string;
  datePaiement?: Date;
  etat?: number;
  statut?: string;
  transactionId?: string;
  motif?: string;
  motifAnnulation?: string;
  dateAnnulation?: Date;
  motifRejet?: string;
  taux?: number;
  duree?: number;
  frequence?: string;
  usager?: string;
  typeRecette?: number | any;
  mode?: number | any;
  // Relations
  contrat?: any;
  typeRecetteData?: any;
  modeData?: any;
  userCreation?: any;
  userAgenceCreate?: any;
  userAnnulation?: any;
}

export interface ArriereElectronicLocal extends BaseSyncEntity {
  contrat_id?: number;
  mois?: number;
  annee?: number;
  moisRestant?: number;
  loyer?: number;
  montantTotal?: number;
  montantRestant?: number;
  estValide?: boolean;
  estPaye?: boolean;
  // Relations
  contrat_idData?: any;
  userCreation?: any;
}

export interface UsagerLocal extends BaseSyncEntity {
  matricule: string;
  nom?: string;
  prenom?: string;
  tel?: string;
  email?: string;
  adresse?: string;
  sexe?: string;
  date_naissance?: Date;
  lieu_naissance?: string;
  ifu?: string;
  npi?: string;
  // Relations
  contrats?: any[];
}

export interface EmplacementLocal extends BaseSyncEntity {
  codeemp: string;
  ncode_emp?: string;
  nref_emp?: string;
  secteur?: string | any;
  montant_location?: number;
  nomLocataire?: string;
  nomExploitant?: string;
  ref_emp?: string;
  // Relations
  secteurData?: any;
  contrats?: any[];
}

export interface UserLocal extends BaseSyncEntity {
  nomComplet?: string;
  telephone?: string;
  sexe?: string;
  email?: string;
  password?: string; // Ne pas synchroniser le mot de passe
  role?: number | any;
  estActif?: boolean;
  // Relations
  roleData?: any;
  userCreation?: any;
}

export interface RoleLocal extends BaseSyncEntity {
  nom?: string;
  description?: string;
  // Relations
  rolePermissions?: any[];
  users?: any[];
}

export interface PermissionLocal extends BaseSyncEntity {
  nom?: string;
  description?: string;
  // Relations
  rolePermissions?: any[];
  userCreation?: any;
}

export interface RolePermissionLocal extends BaseSyncEntity {
  roleId?: number;
  permissionId?: number;
  // Relations
  role?: any;
  permission?: any;
}

export interface ModePaiementLocal extends BaseSyncEntity {
  code?: string;
  libelle?: string;
  // Relations
  userCreation?: any;
}

export interface SecteurLocal extends BaseSyncEntity {
  code_secteur?: string;
  libelle_secteur?: string;
  code_zone?: string;
  use_dpm?: boolean;
  code_secteur_c?: string;
  image_carte_url?: string;
  // Relations
  zone?: any;
  userCreation?: any;
}

export interface ZoneLocal extends BaseSyncEntity {
  code_zone?: string;
  libelle_zone?: string;
  marche_id?: number;
  code_zone_c?: string;
  image_carte_url?: string;
  // Relations
  marche?: any;
  userCreation?: any;
}

export interface MarcheLocal extends BaseSyncEntity {
  nomMarche?: string;
  codeMarche?: string;
  imageCarteUrl?: string;
  // Relations
  userCreation?: any;
}

export interface ActiviteLocal extends BaseSyncEntity {
  raison_sociale?: string;
  sigle?: string;
  ifu?: string;
  rccm?: string;
  produits_ou_services_vendus?: string;
  autres?: string;
  activite_nom?: string;
  categorie_id?: number;
  // Relations
  categorie?: any;
  userCreation?: any;
}

export interface SyncQueue {
  id?: number;
  entityType: string;
  entityId: number | string;
  localId?: string;
  action: 'create' | 'update' | 'delete';
  data: any;
  retryCount: number;
  lastAttempt?: Date;
  error?: string;
  createdAt: Date;
}

// ============================================
// Base de données Dexie
// ============================================

export class OfflineDatabase extends Dexie {
  contrats!: Table<ContratLocal, number>;
  paiements!: Table<PaiementLocal, number>;
  paiementsRecette!: Table<PaiementRecetteLocal, number>;
  arrieresElectronic!: Table<ArriereElectronicLocal, number>;
  usagers!: Table<UsagerLocal, number>;
  emplacements!: Table<EmplacementLocal, number>;
  users!: Table<UserLocal, number>;
  roles!: Table<RoleLocal, number>;
  permissions!: Table<PermissionLocal, number>;
  rolePermissions!: Table<RolePermissionLocal, number>;
  modePaiements!: Table<ModePaiementLocal, number>;
  secteurs!: Table<SecteurLocal, number>;
  zones!: Table<ZoneLocal, number>;
  marches!: Table<MarcheLocal, number>;
  activites!: Table<ActiviteLocal, number>;
  syncQueue!: Table<SyncQueue, number>;

  constructor() {
    super('PaieEsogemaDB');
    
    // Définir le schéma de la base de données
    const db = this as any;
    db.version(1).stores({
      contrats: '++id, localId, codeemp, matricule, syncStatus, lastSync, createdAt, updatedAt',
      paiements: '++id, localId, contrat_id, refPaiement, syncStatus, lastSync, createdAt, updatedAt',
      paiementsRecette: '++id, localId, contrat_id, refPaiement, syncStatus, lastSync, createdAt, updatedAt',
      arrieresElectronic: '++id, localId, contrat_id, mois, annee, syncStatus, lastSync, createdAt, updatedAt',
      usagers: '++id, localId, matricule, syncStatus, lastSync, createdAt, updatedAt',
      emplacements: '++id, localId, codeemp, secteur, syncStatus, lastSync, createdAt, updatedAt',
      users: '++id, localId, telephone, email, syncStatus, lastSync, createdAt, updatedAt',
      roles: '++id, localId, nom, syncStatus, lastSync, createdAt, updatedAt',
      permissions: '++id, localId, nom, syncStatus, lastSync, createdAt, updatedAt',
      rolePermissions: '++id, localId, roleId, permissionId, syncStatus, lastSync, createdAt, updatedAt',
      modePaiements: '++id, localId, code, libelle, syncStatus, lastSync, createdAt, updatedAt',
      secteurs: '++id, localId, code_secteur, code_zone, syncStatus, lastSync, createdAt, updatedAt',
      zones: '++id, localId, code_zone, marche_id, syncStatus, lastSync, createdAt, updatedAt',
      marches: '++id, localId, nomMarche, codeMarche, syncStatus, lastSync, createdAt, updatedAt',
      activites: '++id, localId, ifu, rccm, syncStatus, lastSync, createdAt, updatedAt',
      syncQueue: '++id, entityType, entityId, localId, action, retryCount, createdAt'
    });
  }

  // Helper pour générer un UUID local
  generateLocalId(): string {
    // Générer un UUID v4 simple
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

// Instance singleton de la base de données
export const db = new OfflineDatabase();
